import { useCallback } from 'react'
import type { JSX } from 'react'

import { formatDay, formatDisplayDate, formatDisplayTime } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { formatMoneyForDisplay, parseMinorUnits } from '../money.js'
import { sentence } from '../sentence.js'
import { tenderWord } from '../status-labels.js'

/**
 * The 58mm thermal receipt. §6.7 `POS-011`, §13 `PK-024`, Appendix L.
 *
 * **Printed through the browser, deliberately.** PRD §9 puts "ESC/POS thermal printing without a
 * browser print dialog" in the v2 desktop shell, which is the sentence that makes the v1 path the
 * dialog. So this is a page the browser lays out at 58mm and the shop prints; the escape codes, the
 * drawer kick and the cutter arrive with the shell.
 *
 * **Nothing here decides anything.** Every figure comes from `GET /orders/:orderId/receipt`, which
 * runs `buildSaleReceipt` on the server: the invoice number, the three sum checks and the
 * reversed-sale refusal are all facts about data the server holds, and `MR-04` spends its three
 * rounding points on the write path. A client that re-added a column would be a fourth. So this
 * file is a layout and a print button.
 *
 * **`absent` stays off the paper.** Appendix L's own English wording ("FBR invoice number, logo and
 * QR where applicable") is for the person deciding whether to print, not for a shopkeeper's
 * customer, and it is not translated because it is a reference to a document rather than a
 * sentence. It renders above the slip, in the interface, and the print stylesheet hides it.
 *
 * **Urdu is Naskh here whatever the size.** §12.1: "Naskh always on thermal", because a 58mm head
 * cannot resolve Nastaliq's strokes at any size. `fontForUrdu(size, 'thermal')` is the same rule in
 * `packages/tokens`; the slip states it in CSS by pointing the display face at the UI one inside
 * `.lm-slip`, so every rule underneath inherits it rather than each one remembering.
 */

export interface ReceiptLineData {
  readonly id: string
  readonly description: string
  readonly quantity: number
  /** The whole line before its discounts, which is what the database stores. */
  readonly linePriceMinor: string
  /** Absent where the line price does not divide by the quantity. `later_todo.md` 15. */
  readonly unitPriceMinor?: string
  readonly taxMinor: string
  readonly grossMinor: string
}

export interface ReceiptData {
  readonly orderId: string
  readonly status: string
  readonly shop: {
    readonly name: string
    readonly addressLines: readonly string[]
    readonly taxRegistration?: string
  }
  readonly invoiceNumber: string
  readonly seriesCode: string
  readonly issuedAt: string
  readonly servedByName: string
  /** ISO 4217, shared by every amount on the slip. */
  readonly currency: string
  readonly lines: readonly ReceiptLineData[]
  readonly subtotalMinor: string
  readonly orderDiscountMinor: string
  readonly taxMinor: string
  readonly totalMinor: string
  readonly tenders: readonly { readonly method: string; readonly amountMinor: string }[]
  readonly tenderedMinor: string
  /** `POS-005`: what a refund gave back, beside the invoice and never netted into it. */
  readonly refundedMinor: string
  readonly balanceDueMinor: string
  readonly settled: boolean
  readonly promisedOn?: string
  readonly policyVersion?: string
  readonly verificationNote?: string
  /**
   * `TAX-005`: the fiscal number and QR, where a regime mandates them. `PK-002`, Appendix L.
   *
   * Present only for a confirmed record, which the server decides: `PK-004` makes a queued
   * transmission a normal resting state, and an unconfirmed number printed here would scan and
   * fail against FBR's own portal, telling the buyer the shop is lying rather than waiting.
   *
   * Absent for every shop outside a regime, which `PK-002` says is the typical independent
   * optician, and `absent` then carries the Appendix L line as it always has.
   */
  readonly fbr?: { readonly invoiceNumber: string; readonly qrPayload: string }
  /** Appendix L content this shop's data cannot supply, in the appendix's own words. */
  readonly absent: readonly string[]
}

export interface ReceiptProps {
  readonly receipt: ReceiptData
  /** `BR-01`: the branch's own zone. Appendix L asks for the date and time of the sale. */
  readonly timezone: string
  /** Injectable so a test can assert the print was asked for without opening a dialog. */
  readonly print?: () => void
}

const amount = (minor: string, currency: string): string =>
  formatMoneyForDisplay(parseMinorUnits(minor), currency)

export function Receipt({ receipt, timezone, print }: ReceiptProps): JSX.Element {
  const { t, locale } = useTranslation()

  const onPrint = useCallback(() => {
    if (print) {
      print()
      return
    }
    // The dialog, which is the v1 path. Guarded because jsdom has no `print` and a test that opened
    // one would hang.
    if (typeof window !== 'undefined' && typeof window.print === 'function') window.print()
  }, [print])

  const instant = Date.parse(receipt.issuedAt)
  const when = Number.isFinite(instant)
    ? `${formatDisplayDate(instant, timezone, locale)} ${formatDisplayTime(instant, timezone, locale)}`
    : receipt.issuedAt

  return (
    <section className="lm-screen" aria-labelledby="receipt-heading">
      <h1 id="receipt-heading" className="lm-screen__heading">
        {t('receipt.heading')}
      </h1>

      <div className="lm-screen__actions lm-no-print">
        <button type="button" className="lm-button lm-button--primary" onClick={onPrint}>
          {t('receipt.print')}
        </button>
      </div>

      {receipt.absent.length === 0 ? null : (
        <div className="lm-no-print" role="status">
          {/* One translated sentence with a count, then the appendix's own words. A shopkeeper
              needs to know the document is incomplete; whoever fixes it needs the field names. */}
          <p className="lm-hint">{t('receipt.incomplete', { count: receipt.absent.length })}</p>
          <ul className="lm-hint">
            {receipt.absent.map((field) => (
              <li key={field} dir="ltr">
                {field}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* The slip. 58mm wide, monospaced, and the only thing the print stylesheet keeps. */}
      <article className="lm-slip" aria-label={t('receipt.slipLabel')}>
        <header className="lm-slip__head">
          <p className="lm-slip__shop">{receipt.shop.name}</p>
          {receipt.shop.addressLines.map((line) => (
            <p key={line} className="lm-slip__address">
              {line}
            </p>
          ))}
          {receipt.shop.taxRegistration === undefined ? null : (
            <p className="lm-slip__line">
              {sentence(t, 'receipt.taxRegistration', {
                registration: <Ltr>{receipt.shop.taxRegistration}</Ltr>,
              })}
            </p>
          )}
        </header>

        <p className="lm-slip__line">
          {sentence(t, 'receipt.invoice', {
            series: <Ltr>{receipt.seriesCode}</Ltr>,
            number: <Ltr>{receipt.invoiceNumber}</Ltr>,
          })}
        </p>
        <p className="lm-slip__line">{sentence(t, 'receipt.when', { when: <Ltr>{when}</Ltr> })}</p>
        <p className="lm-slip__line">{t('receipt.servedBy', { name: receipt.servedByName })}</p>

        <table className="lm-slip__lines">
          <caption className="lm-visually-hidden">{t('receipt.linesCaption')}</caption>
          <tbody>
            {receipt.lines.map((line) => (
              <tr key={line.id}>
                <th scope="row" className="lm-slip__description">
                  {line.description}
                  <span className="lm-slip__qty" dir="ltr">
                    {line.unitPriceMinor === undefined
                      ? // No unit price in the database for this line, so the quantity stands alone
                        // rather than beside a figure a customer would multiply it by.
                        t('receipt.qtyOnly', { quantity: line.quantity })
                      : t('receipt.qtyAt', {
                          quantity: line.quantity,
                          unit: amount(line.unitPriceMinor, receipt.currency),
                        })}
                  </span>
                  <span className="lm-slip__tax" dir="ltr">
                    {t('receipt.lineTax', { tax: amount(line.taxMinor, receipt.currency) })}
                  </span>
                </th>
                <td className="lm-slip__amount" dir="ltr">
                  {amount(line.grossMinor, receipt.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <dl className="lm-slip__totals">
          <dt>{t('receipt.subtotal')}</dt>
          <dd dir="ltr">{amount(receipt.subtotalMinor, receipt.currency)}</dd>
          <dt>{t('receipt.discount')}</dt>
          <dd dir="ltr">{amount(receipt.orderDiscountMinor, receipt.currency)}</dd>
          <dt>{t('receipt.tax')}</dt>
          <dd dir="ltr">{amount(receipt.taxMinor, receipt.currency)}</dd>
          <dt className="lm-slip__grand">{t('receipt.total')}</dt>
          <dd className="lm-slip__grand" dir="ltr">
            {amount(receipt.totalMinor, receipt.currency)}
          </dd>
        </dl>

        <dl className="lm-slip__totals">
          {receipt.tenders.map((tender, index) => (
            <div key={`${tender.method}-${String(index)}`} className="lm-slip__tender">
              <dt>{tenderWord(t, tender.method)}</dt>
              <dd dir="ltr">{amount(tender.amountMinor, receipt.currency)}</dd>
            </div>
          ))}
          {/* `POS-005`: beside the invoice, never netted into it, and only when there is one. */}
          {receipt.refundedMinor === '0' ? null : (
            <>
              <dt>{t('receipt.refunded')}</dt>
              <dd dir="ltr">{amount(receipt.refundedMinor, receipt.currency)}</dd>
            </>
          )}
          <dt>{t('receipt.balanceDue')}</dt>
          <dd dir="ltr">{amount(receipt.balanceDueMinor, receipt.currency)}</dd>
        </dl>

        {/* The one sentence the slip must get right. `settled` is the server's, and it is false on a
            refunded sale even though nothing is due: the goods came back and the money went back. */}
        <p className="lm-slip__status">
          {receipt.settled ? t('receipt.paidInFull') : t('receipt.notSettled')}
        </p>

        {receipt.promisedOn === undefined ? null : (
          <p className="lm-slip__line">
            {sentence(t, 'receipt.promised', { date: <Ltr>{formatDay(receipt.promisedOn, timezone, locale)}</Ltr> })}
          </p>
        )}

        {receipt.policyVersion === undefined ? null : (
          <p className="lm-slip__line">
            {sentence(t, 'receipt.policy', { version: <Ltr>{receipt.policyVersion}</Ltr> })}
          </p>
        )}

        {receipt.verificationNote === undefined ? null : (
          <p className="lm-slip__line">{receipt.verificationNote}</p>
        )}

        {receipt.fbr === undefined ? null : (
          <div className="lm-slip__fiscal">
            {/* `PK-002`: a compliant receipt carries the FBR invoice number and a verifiable QR
                code. Latin and never mirrored: the number is read out and the payload is scanned,
                and either reversed in an Urdu page is a different value (`LOC-005`). */}
            <p className="lm-slip__line" dir="ltr">
              {sentence(t, 'receipt.fbrNumber', { number: <Ltr>{receipt.fbr.invoiceNumber}</Ltr> })}
            </p>
            {/* The payload as text, not as an image. A QR renderer is a dependency this build does
                not have, and the FBR logo `PK-002` also asks for is an asset this repository does
                not hold: both stay in `absent` rather than being invented. What the payload does
                carry is what a buyer can type into FBR's portal, which is the fallback the portal
                itself offers. `later_todo.md` records the gap. */}
            <p className="lm-slip__fiscalPayload" dir="ltr">
              {receipt.fbr.qrPayload}
            </p>
          </div>
        )}
      </article>
    </section>
  )
}
